'use client';

import { useTimezoneChoices } from '@/hooks/use-timezone-choices';
import { timezoneDisplayLabel } from '@/lib/timezone';
import { Select } from './input';

interface TimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * The standard timezone picker — the region's timezone choices (Admin Settings
 * → Region) with the live seasonal abbreviation (e.g. "Central (CDT)"). This is the single dropdown used on
 * Insights, Activities, and every other ranged/analytics page so the option
 * list and DST handling stay identical everywhere; pair it with
 * usePreferredTimezone to remember the choice across pages.
 */
export function TimezoneSelect({
  value,
  onChange,
  disabled,
  className = 'w-auto min-w-[9.5rem]',
  'aria-label': ariaLabel = 'Timezone',
}: TimezoneSelectProps) {
  const regionChoices = useTimezoneChoices();
  // A saved zone the region no longer offers stays listed, so the select
  // shows what is stored instead of silently displaying the first choice.
  const choices =
    value && !regionChoices.some((t) => t.value === value) ? [...regionChoices, { value, label: value }] : regionChoices;
  return (
    <Select
      aria-label={ariaLabel}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={className}
    >
      {choices.map((t) => (
        <option key={t.value} value={t.value}>
          {timezoneDisplayLabel(t.value)}
        </option>
      ))}
    </Select>
  );
}
